// Custom TutorialStep interface (library-agnostic)
export interface TutorialStep {
  element?: string; // CSS selector for the target element
  popover?: {
    title?: string;
    description?: string;
    side?: 'top' | 'bottom' | 'left' | 'right' | 'over';
    align?: 'start' | 'center' | 'end';
  };
  page?: 'dashboard' | 'knowledge-base' | 'training' | 'training-builder' | 'calendar' | 'settings';
  requiresNavigation?: boolean;
  navigationTarget?: string;
  adminOnly?: boolean;
  clickSelector?: string; // Selector to click before showing this step (e.g., to switch tabs)
}

// Steps shown on the Dashboard page
export const dashboardSteps: TutorialStep[] = [
  {
    popover: {
      title: 'Welcome to Apexia Labs!',
      description: 'Let us show you around the key features that will help you and your team succeed in sales conversations.',
      side: 'over' as const,
      align: 'center' as const,
    },
    page: 'dashboard',
  },
  {
    element: '[data-tutorial="insights-section"]',
    popover: {
      title: 'Insights & Analytics',
      description: 'The Insights section gives you powerful analytics. View performance metrics across your entire organization or drill down to individual team members.',
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
  },
  {
    element: '[data-tutorial="sessions-link"]',
    popover: {
      title: 'Session History',
      description: 'Sessions shows your complete call history. Review transcripts, see AI-detected objections, FAQs, and get detailed performance insights for each conversation.',
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
  },
  {
    element: '[data-tutorial="live-call-button"]',
    popover: {
      title: 'Live Call Assistant',
      description: 'Live Call lets you view real-time transcripts during active sales calls. Get instant AI assistance with objection handling, talking points, and competitor comparisons.',
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
  },
  {
    element: '[data-tutorial="knowledge-section"]',
    popover: {
      title: 'Knowledge Management',
      description: "The Knowledge section is where you build your AI assistant's brain. Let's take a closer look at how to set up your knowledge base!",
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
    requiresNavigation: true,
    navigationTarget: '/knowledge-base',
    adminOnly: true,
  },
];

// Steps shown on the Knowledge Base page
export const knowledgeBaseSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="knowledge-sources"]',
    popover: {
      title: 'Add Knowledge',
      description: 'Add to your current knowledge draft from a website scan, a spreadsheet, or guided setup. Nothing goes live until you review and publish it.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'knowledge-base',
  },
  {
    element: '[data-tutorial="bulk-upload"]',
    popover: {
      title: 'Bulk Upload',
      description: 'Have existing content? Use Bulk Upload to import objections, FAQs, and more from Excel spreadsheets. Perfect for migrating existing training materials.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'knowledge-base',
  },
  {
    element: '[data-tutorial="knowledge-tabs"]',
    popover: {
      title: 'Knowledge Categories',
      description: `Organize your knowledge into four categories:<br><br><b>Objections:</b> Objections are common pushbacks from prospects. Define questions and responses here so the AI can help your team handle them effectively in real-time.<br><br><b>FAQs:</b> FAQs store frequently asked questions and their answers. The AI uses these to provide instant, accurate information during sales calls.<br><br><b>Landmines:</b> Landmines are sensitive topics to avoid or handle carefully. The AI will alert your team when these come up in conversation and provide guidance.<br><br><b>Competitors:</b> Track competitor mentions and equip your team with instant comparison points and differentiators when competitors come up in sales calls.`,
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'knowledge-base',
    requiresNavigation: true,
    navigationTarget: '/training',
    adminOnly: true,
  },
];

// Steps shown on the Training page
export const trainingSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="training-tracks-tab"]',
    popover: {
      title: 'Training Tracks',
      description: 'Training Tracks let you create structured practice scenarios for your sales team. Combine objections, FAQs, landmines, and competitors into realistic training sessions.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'training',
  },
  {
    element: '[data-tutorial="create-track-button"]',
    popover: {
      title: 'Create Training Track',
      description: "Click here to create a new training track. Let's explore the track builder to see how it works!",
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'training',
    requiresNavigation: true,
    navigationTarget: '/training/builder',
    adminOnly: true,
  },
];

// Steps shown on the Training Track Builder page
export const trainingBuilderSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="track-details-tab"]',
    popover: {
      title: 'Track Details',
      description: 'Start by configuring your track settings. Give it a name, description, and set up the training parameters.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'training-builder',
  },
  {
    element: '[data-tutorial="track-type-select"]',
    popover: {
      title: 'Track Type',
      description: `<b>Knowledge Base Items:</b> The AI customer reads out each item you add, one at a time and in your order. The rep answers each one, and every answer is scored after the session.<br><br><b>Sales Playbook:</b> A live, free-flowing call with an AI customer you describe. These tracks also have a Difficulty setting, which sets how accurate an answer must be and how often the customer pushes back.`,
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'training-builder',
  },
  {
    element: '[data-tutorial="add-items-buttons"]',
    popover: {
      title: 'Add Knowledge Base Items',
      description: 'Add Objections, FAQs, Landmines, or Competitors to your training track. Mix different types to create comprehensive training scenarios.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'training-builder',
    clickSelector: '[data-tutorial="training-items-tab"]', // Switch to Training Items tab first
  },
  {
    element: '[data-tutorial="items-list-area"]',
    popover: {
      title: 'Drag & Drop Reordering',
      description: 'Once you add items, they appear here. Drag and drop to reorder them - the AI will present items in this sequence during training.',
      side: 'top' as const,
      align: 'start' as const,
    },
    page: 'training-builder',
  },
  {
    element: '[data-tutorial="user-assignment-tab"]',
    popover: {
      title: 'User Assignment',
      description: "Assign users to this training track. Only assigned users will see this track in their training list. Now let's check out Calendar Integration!",
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'training-builder',
    clickSelector: '[data-tutorial="user-assignment-tab"]', // Switch to User Assignment tab first
    requiresNavigation: true,
    navigationTarget: '/calendar',
    adminOnly: true,
  },
];

// Steps shown on the Calendar page
export const calendarSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="calendar-integrations"]',
    popover: {
      title: 'Calendar Integrations',
      description: 'Connect your Google Calendar to automatically sync meetings. The AI assistant can join your scheduled calls automatically.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'calendar',
  },
  {
    element: '[data-tutorial="auto-join-settings"]',
    popover: {
      title: 'Auto-Join Settings',
      description: "Configure when and how the AI bot joins your meetings. Set the bot name and how many minutes before the meeting it should join. Now let's check out Settings!",
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'calendar',
    requiresNavigation: true,
    navigationTarget: '/settings',
    adminOnly: true,
  },
];

// Steps shown on the Settings page (final page)
export const settingsSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="speech-rate-section"]',
    popover: {
      title: 'Speech Rate Monitoring',
      description: "Monitor your team's speaking pace during calls. Set thresholds to alert when someone is speaking too fast, helping improve communication clarity.",
      side: 'left' as const,
      align: 'start' as const,
    },
    page: 'settings',
  },
  {
    element: '[data-tutorial="tutorial-section"]',
    popover: {
      title: 'Restart Tutorial',
      description: "You're all set! Your AI assistant is ready to help your team succeed. You can restart this guided tour anytime from here.",
      side: 'left' as const,
      align: 'start' as const,
    },
    page: 'settings',
  },
];

// ============================================
// NON-ADMIN USER TUTORIAL STEPS (6 steps total)
// ============================================

// Non-admin dashboard steps (Steps 1-4)
export const nonAdminDashboardSteps: TutorialStep[] = [
  {
    // Step 1: Welcome
    popover: {
      title: 'Welcome to Apexia Labs!',
      description: 'Let us show you around the key features that will help you succeed in your sales conversations.',
      side: 'over' as const,
      align: 'center' as const,
    },
    page: 'dashboard',
  },
  {
    // Step 2: Your Performance
    element: '[data-tutorial="insights-section"]',
    popover: {
      title: 'Your Performance',
      description: 'Track your personal sales performance here. View your call metrics, success rates, and identify areas where you can improve.',
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
  },
  {
    // Step 3: Your Call History
    element: '[data-tutorial="sessions-link"]',
    popover: {
      title: 'Your Call History',
      description: 'Access your past sales calls here. Review transcripts, see AI-detected objections and FAQs from your conversations, and learn from each call to improve your skills.',
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
  },
  {
    // Step 4: Live Call Assistant - navigates to training page
    element: '[data-tutorial="live-call-button"]',
    popover: {
      title: 'Live Call Assistant',
      description: "Get real-time AI assistance during your sales calls. View live transcripts and receive instant help with objection handling, talking points, and competitor comparisons. Now let's check out your training!",
      side: 'right' as const,
      align: 'start' as const,
    },
    page: 'dashboard',
    requiresNavigation: true,
    navigationTarget: '/training',
  },
];

// Non-admin training step (Step 6 - navigates to Calendar)
export const nonAdminTrainingStep: TutorialStep = {
  element: '[data-tutorial="training-sessions-tab"]',
  popover: {
    title: 'Your Training Sessions',
    description: "This is where you'll find training tracks assigned to you. Click on any track to start a practice session with the AI, and track your progress as you improve your sales skills. Now let's check out Calendar settings!",
    side: 'bottom' as const,
    align: 'start' as const,
  },
  page: 'training',
  clickSelector: '[data-tutorial="training-sessions-tab"]', // Switch to Training Sessions tab
  requiresNavigation: true,
  navigationTarget: '/calendar',
};

// Non-admin calendar steps (Steps 7-8)
export const nonAdminCalendarSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="calendar-integrations"]',
    popover: {
      title: 'Calendar Integrations',
      description: 'Connect your Google Calendar to automatically sync your meetings. The AI assistant can join your scheduled calls automatically to help you during sales conversations.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'calendar',
  },
  {
    element: '[data-tutorial="auto-join-settings"]',
    popover: {
      title: 'Auto-Join Settings',
      description: "Configure when and how the AI bot joins your meetings. Set how many minutes before the meeting it should join. Now let's check out Settings!",
      side: 'bottom' as const,
      align: 'start' as const,
    },
    page: 'calendar',
    requiresNavigation: true,
    navigationTarget: '/settings',
  },
];

// Non-admin settings steps (Step 8 - Final)
export const nonAdminSettingsSteps: TutorialStep[] = [
  {
    element: '[data-tutorial="tutorial-section"]',
    popover: {
      title: 'Restart Tutorial',
      description: "You're all set! Your AI assistant is ready to help you succeed. You can restart this guided tour anytime from here.",
      side: 'left' as const,
      align: 'start' as const,
    },
    page: 'settings',
  },
];

// Get all steps for a given role
// Admin Order: Dashboard (5) -> Knowledge Base (3) -> Training (2) -> Training Builder (5) -> Calendar (2) -> Settings (2) = 19 steps
// Non-Admin Order: Dashboard (4) -> Training Sessions (1) -> Calendar (2) -> Settings (1) = 8 steps
export const getStepsForRole = (isAdmin: boolean): TutorialStep[] => {
  if (isAdmin) {
    return [...dashboardSteps, ...knowledgeBaseSteps, ...trainingSteps, ...trainingBuilderSteps, ...calendarSteps, ...settingsSteps];
  }
  // Non-admins: Dashboard -> Training Sessions -> Calendar -> Settings
  return [...nonAdminDashboardSteps, nonAdminTrainingStep, ...nonAdminCalendarSteps, ...nonAdminSettingsSteps];
};

// Get dashboard-only steps for a given role
export const getDashboardSteps = (isAdmin: boolean): TutorialStep[] => {
  if (isAdmin) {
    return dashboardSteps;
  }
  // Non-admins: use dedicated non-admin dashboard steps (includes navigation to training)
  return nonAdminDashboardSteps;
};

// Get non-admin training step (for use in TutorialContext)
export const getNonAdminTrainingStep = (): TutorialStep => {
  return nonAdminTrainingStep;
};

// Get total step count
export const getTotalStepCount = (isAdmin: boolean): number => {
  return getStepsForRole(isAdmin).length;
};

// Get steps count up to a specific page (for progress calculation)
// Admin Order: Dashboard -> Knowledge Base -> Training -> Training Builder -> Calendar -> Settings
// Non-Admin Order: Dashboard -> Training -> Calendar -> Settings
export const getStepsCountBeforePage = (page: string, isAdmin: boolean): number => {
  if (isAdmin) {
    let count = 0;
    if (page === 'knowledge-base') {
      count = dashboardSteps.length;
    } else if (page === 'training') {
      count = dashboardSteps.length + knowledgeBaseSteps.length;
    } else if (page === 'training-builder') {
      count = dashboardSteps.length + knowledgeBaseSteps.length + trainingSteps.length;
    } else if (page === 'calendar') {
      count = dashboardSteps.length + knowledgeBaseSteps.length + trainingSteps.length + trainingBuilderSteps.length;
    } else if (page === 'settings') {
      count = dashboardSteps.length + knowledgeBaseSteps.length + trainingSteps.length + trainingBuilderSteps.length + calendarSteps.length;
    }
    return count;
  }

  // Non-admin: Dashboard (4) -> Training (1) -> Calendar (2) -> Settings (1)
  let count = 0;
  if (page === 'training') {
    count = nonAdminDashboardSteps.length; // 4
  } else if (page === 'calendar') {
    count = nonAdminDashboardSteps.length + 1; // 4 + 1 training step = 5
  } else if (page === 'settings') {
    count = nonAdminDashboardSteps.length + 1 + nonAdminCalendarSteps.length; // 4 + 1 + 2 = 7
  }
  return count;
};

// Get non-admin calendar steps
export const getNonAdminCalendarSteps = (): TutorialStep[] => {
  return nonAdminCalendarSteps;
};

// Get non-admin settings steps
export const getNonAdminSettingsSteps = (): TutorialStep[] => {
  return nonAdminSettingsSteps;
};
